import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Tabs, Tab } from '@mui/material';
import { ArrowBackOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';
import { openSignedUrl, fetchSignedUrl } from '../utils/s3';

function SignedImage({ fileKey, label }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    fetchSignedUrl(fileKey).then(setUrl);
  }, [fileKey]);

  if (!url) return <div style={{ height: 60, width: 80, background: '#f5f5f5', borderRadius: 4 }} />;

  return (
    <img
      src={url}
      alt={label}
      style={{ height: 50, width: 80, objectFit: 'cover', border: '1px solid #e0e0e0', borderRadius: 4, cursor: 'pointer' }}
      onClick={() => window.open(url, '_blank')}
    />
  );
}

function FieldItem({ label, value, isLink, fullWidth }) {
  return (
    <div style={{ padding: '14px 0', borderBottom: '1px solid #e8e8e8', ...(fullWidth && { gridColumn: '1 / -1' }), overflow: 'hidden' }}>
      <span style={{ fontWeight: 600, color: '#1a1d29', fontSize: 14 }}>{label} : </span>
      {isLink && value ? (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer" style={{ color: '#019383', fontSize: 13, wordBreak: 'break-all', textDecoration: 'none' }}>{value}</a>
      ) : (
        <span style={{ color: '#444', fontSize: 14 }}>{value || '-'}</span>
      )}
    </div>
  );
}

function SectionGrid({ fields }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 20px' }}>
      {fields.map((f, i) => (
        <FieldItem key={i} label={f.label} value={f.value} isLink={f.isLink} fullWidth={f.fullWidth} />
      ))}
    </div>
  );
}

function DocItem({ filename, label }) {
  if (!filename) return null;
  const ext = filename.split('.').pop().toLowerCase();
  const isImage = ['jpg', 'jpeg', 'png', 'webp', 'jfif'].includes(ext);

  return (
    <div style={{ padding: '18px 0', borderBottom: '1px solid #e8e8e8' }}>
      <div style={{ fontWeight: 600, color: '#1a1d29', fontSize: 14, marginBottom: 8 }}>{label}</div>
      {isImage ? (
        <SignedImage fileKey={filename} label={label} />
      ) : (
        <div
          style={{ height: 50, width: 70, border: '1px solid #e0e0e0', borderRadius: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f8f8' }}
          onClick={() => openSignedUrl(filename)}
        >
          <span style={{ fontSize: 11, color: '#555' }}>📄 {ext.toUpperCase()}</span>
        </div>
      )}
    </div>
  );
}

function InfluencerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [influencer, setInfluencer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchInfluencer();
  }, [id]);

  const fetchInfluencer = async () => {
    try {
      const response = await api.get(`/influencers/${id}`);
      setInfluencer(response.data);
    } catch (error) {
      toast.error('Failed to fetch influencer details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getFullYear()}`;
  };

  if (loading) return <div style={{ padding: 24, color: '#666' }}>Loading...</div>;
  if (!influencer) return <div style={{ padding: 24, color: '#666' }}>Influencer not found</div>;

  const showInstagram = influencer.socialMediaPlatforms?.includes('instagram') || influencer.instagramFollowers;
  const showYoutube = influencer.socialMediaPlatforms?.includes('youtube') || influencer.youtubeFollowers;

  const tabs = [
    'Basic Details',
    'Contact',
    // 'Platform',
    ...(showInstagram ? ['Instagram'] : []),
    ...(showYoutube ? ['YouTube'] : []),
    ...(influencer.panNumber ? ['Banking'] : []),
    'Documents',

  ];

  const tabContentMap = {
    'Basic Details': (
      <SectionGrid fields={[
        { label: 'Name', value: influencer.name },
        { label: 'Email', value: influencer.email },
        { label: 'Gender', value: influencer.gender },
        { label: 'Date of Birth', value: formatDate(influencer.dateOfBirth) },
        { label: 'Age', value: influencer.age },
        { label: 'Location', value: influencer.location },
        { label: 'Language', value: influencer.language },
        { label: 'Category', value: influencer.category },
        { label: 'Brand Collaboration', value: influencer.brandCollaboration },
        { label: 'Platform Type', value: influencer.platformType },
        { label: 'Social Media Platforms', value: influencer.socialMediaPlatforms },
      ]} />
    ),
    'Contact': (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0 20px' }}>
          <FieldItem label="Contact Number" value={influencer.contactNumber} />
          <FieldItem label="Pincode" value={influencer.pincode} />
          <FieldItem label="State" value={influencer.state} />
          <FieldItem label="City" value={influencer.city} />
        </div>
        <div>
          <FieldItem label="Address" value={influencer.address} />
          <FieldItem label="Instagram Link" value={influencer.instagramLink} isLink />
          <FieldItem label="YouTube Link" value={influencer.youtubeLink} isLink />
        </div>
      </>
    ),
    // 'Platform': (
    //   <SectionGrid fields={[
    //     { label: 'Platform Type', value: influencer.platformType },
    //     { label: 'Social Media Platforms', value: influencer.socialMediaPlatforms },
    //   ]} />
    // ),
    'Instagram': (
      <SectionGrid fields={[
        { label: 'Followers', value: influencer.instagramFollowers?.toLocaleString() },
        { label: 'Content Cost (Reel + Story)', value: influencer.reelCost ? `₹${influencer.reelCost}` : '' },
        { label: 'No of Posts', value: influencer.instagramNoOfPost },
        { label: 'Views', value: influencer.last10ReelsViews },
        { label: 'Reach', value: influencer.last10ReelsReach },
        { label: 'Likes', value: influencer.instagramLikes },
        { label: 'Comments', value: influencer.instagramComments },
        { label: 'Saves', value: influencer.instagramSaves },
        { label: 'Shares', value: influencer.instagramShares },
        { label: 'Reposts', value: influencer.instagramReposts },
      ]} />
    ),
    'YouTube': (
      <SectionGrid fields={[
        { label: 'Followers', value: influencer.youtubeFollowers?.toLocaleString() },
        { label: 'YouTube Type', value: influencer.youtubeType },
        ...(influencer.youtubeType === 'Post' ? [
          { label: 'Video Cost', value: influencer.youtubeVideoCost ? `₹${influencer.youtubeVideoCost}` : '' },
          { label: 'No. Of Posts (Past 30 days)', value: influencer.youtubeNoOfPost },
        ] : []),
        ...(influencer.youtubeType === 'Shorts' ? [
          { label: 'Shorts Cost', value: influencer.youtubeShortsCost ? `₹${influencer.youtubeShortsCost}` : '' },
          { label: 'No. Of Shorts (Past 30 days)', value: influencer.youtubeNoOfShorts },
        ] : []),
        { label: 'Views', value: influencer.last10ShortsViews },
        { label: 'Reach', value: influencer.last10ShortsReach },
        { label: 'Likes', value: influencer.youtubeLikes },
        { label: 'Comments', value: influencer.youtubeComments },
        { label: 'Shares', value: influencer.youtubeShares },
      ]} />
    ),
    'Banking': (
      <SectionGrid fields={[
        { label: 'PAN Number', value: influencer.panNumber },
        { label: 'Aadhar Number', value: influencer.aadharNumber },
        { label: 'Bank Account Number', value: influencer.bankAccountNumber },
        { label: 'IFSC Code', value: influencer.ifscCode },
        { label: 'Account Holder Name', value: influencer.holderName },
        { label: 'Bank Name', value: influencer.bankName },
      ]} />
    ),
    'Documents': (() => {
      const instagramDocs = [
        [influencer.instagramInteractionScreenshot, 'Instagram Interactions'],
        [influencer.instagramViewReachScreenshot, 'Instagram View & Reach'],
        [influencer.instagramFollowersScreenshot, 'Instagram Followers'],
        [influencer.instagramGenderScreenshot, 'Instagram Gender'],
        [influencer.instagramAgeScreenshot, 'Instagram Age'],
        [influencer.instagramDemographyScreenshot, 'Instagram Demography'],
      ].filter(([file]) => file);

      const youtubeDocs = [
        [influencer.youtubeViewReachScreenshot, 'YouTube Views & Reach'],
        [influencer.youtubeSubscribersScreenshot, 'YouTube Subscribers'],
        [influencer.youtubeAgeGenderScreenshot, 'YouTube Age & Gender'],
        [influencer.youtubeDemographyScreenshot, 'YouTube Demography'],
      ].filter(([file]) => file);

      const bankingDocs = [
        [influencer.panCardImage, 'PAN Card'],
        [influencer.aadharCardImage, 'Aadhar Card'],
        [influencer.bankPassbookImage, 'Bank Passbook'],
        [influencer.gstCertificate, 'GST Certificate'],
      ].filter(([file]) => file);

      return (
        <div>
          {instagramDocs.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1d29', mb: 1, mt: 1 }}>Instagram</Typography>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 20px' }}>
                {instagramDocs.map(([file, label]) => <DocItem key={label} filename={file} label={label} />)}
              </div>
            </>
          )}
          {youtubeDocs.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1d29', mb: 1, mt: 2 }}>YouTube</Typography>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 20px' }}>
                {youtubeDocs.map(([file, label]) => <DocItem key={label} filename={file} label={label} />)}
              </div>
            </>
          )}
          {bankingDocs.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1d29', mb: 1, mt: 2 }}>Banking</Typography>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0 20px' }}>
                {bankingDocs.map(([file, label]) => <DocItem key={label} filename={file} label={label} />)}
              </div>
            </>
          )}
          {instagramDocs.length === 0 && youtubeDocs.length === 0 && bankingDocs.length === 0 && (
            <Typography variant="body2" color="textSecondary">No documents uploaded.</Typography>
          )}
        </div>
      );
    })(),
    'Additional': (
      <SectionGrid fields={[
        { label: 'Delivery Address Same', value: influencer.deliveryAddressSame },
        ...(influencer.deliveryAddress ? [{ label: 'Delivery Address', value: influencer.deliveryAddress }] : []),
        ...(influencer.remarks ? [{ label: 'Remarks', value: influencer.remarks }] : []),
      ]} />
    ),
  };

  const renderContent = () => tabContentMap[tabs[activeTab]] || null;

  return (
    <div style={{ padding: 24, background: '#fff', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        {/* <Button
          startIcon={<ArrowBackOutlined />}
          onClick={() => navigate('/onboard-forms')}
          sx={{ mr: 2 }}
        >
          Back
        </Button> */}
        <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
          Profile Details
        </Typography>
      </div>

      {/* Tabs */}
      <Box sx={{ borderBottom: '2px solid #e8e8e8', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: 14, color: '#666' },
            '& .Mui-selected': { color: '#019383 !important', fontWeight: 600 },
            '& .MuiTabs-indicator': { backgroundColor: '#019383' },
          }}
        >
          {tabs.map((tab, i) => <Tab key={i} label={tab} />)}
        </Tabs>
      </Box>

      {/* Section Title */}
      {/* <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1d29', mb: 1 }}>
        {tabs[activeTab]}
      </Typography> */}
      {/* <div style={{ borderBottom: '1px solid #e8e8e8', marginBottom: 24 }} /> */}

      {/* Content */}
      {renderContent()}
    </div>
  );
}

export default InfluencerDetail;
